import React from 'react';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import styles from './styles.module.scss';

interface MainSectionProps {
  title?: string;
  subtitle?: string;
  logoUrl?: string;
  logoAlt?: string;
  headerRight?: React.ReactNode;
  /**
   * When true, hides `title`, `logoUrl`, `logoAlt` on desktop (≥lg breakpoint, 1024px) because
   * the sidebar (e.g. TournamentInfoCard) already renders the same content there. `subtitle` and
   * `headerRight` remain visible — they're tab-specific. On mobile/tablet (< lg) the sidebar is
   * `display: none`, so all fields render.
   */
  hideTitleOnDesktop?: boolean;
  /**
   * When true, hides `title`, `logoUrl`, `logoAlt` on mobile/tablet (<lg, 1024px) because the
   * page-level `PageHeader` (driven by `usePageConfig`) already renders the same page title
   * there and the duplication burns vertical space on a phone. `subtitle` and `headerRight`
   * (e.g. "Создать" CTA) remain visible. Desktop continues to show the title — there's room.
   */
  hideTitleOnMobile?: boolean;
  /**
   * When true, hides `subtitle` below the tablet breakpoint (<768px). Use this when the active
   * tab nav already announces the same word (e.g. "Матчи") and the subtitle would be redundant
   * on a phone-sized viewport. Tablet and desktop continue to show the subtitle.
   */
  subtitleHideOnMobile?: boolean;
  /**
   * When true, the section claims exactly the available height of its parent (`flex: 1; min-height: 0`)
   * instead of the default `flex: 1 0 auto` (grows to the slide, never below its content, and relies
   * on the outer `slideContent` scroll container). Required for tabs whose body owns its own scroll container
   * (e.g. MatchesTab's continuous round scroller, `.matchesScroll`), since that container needs a
   * bounded height to be scrollable. Without this prop the body resolves to content height, the
   * inner scroll container collapses with the full content rendered statically, and the user
   * loses the windowed-scroll UX.
   */
  fill?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const MainSection: React.FC<MainSectionProps> = ({
  title,
  subtitle,
  logoUrl,
  logoAlt,
  headerRight,
  hideTitleOnDesktop = false,
  hideTitleOnMobile = false,
  subtitleHideOnMobile = false,
  fill = false,
  children,
  className,
}) => {
  const isMobile = useBreakpointDown('lg');
  const hideTitle = (hideTitleOnDesktop && !isMobile) || (hideTitleOnMobile && isMobile);
  const showTitle = !hideTitle;
  const effectiveTitle = showTitle ? title : undefined;
  const effectiveLogoUrl = showTitle ? logoUrl : undefined;
  const hasHeader = effectiveTitle || subtitle || effectiveLogoUrl || headerRight;

  return (
    <div className={`${styles.section} ${fill ? styles.sectionFill : ''} ${className || ''}`}>
      {hasHeader && (
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <div className={styles.titleSection}>
              {effectiveLogoUrl && (
                <img
                  src={effectiveLogoUrl}
                  alt={logoAlt || ''}
                  className={styles.logo}
                />
              )}
              <div className={styles.titleText}>
                {effectiveTitle && <h2 className={styles.title}>{effectiveTitle}</h2>}
                {subtitle && (
                  <p className={`${styles.subtitle} ${subtitleHideOnMobile ? styles.subtitleHideOnMobile : ''}`}>
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
            {headerRight}
          </div>
        </div>
      )}
      <div className={styles.body}>
        {children}
      </div>
    </div>
  );
};

export default MainSection;
